import { Controller } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { type SxProps, type Theme } from "@mui/material/styles";

import { Box } from "ui/box";
import { Button } from "ui/button";
import { Dialog, DialogActions } from "ui/dialog";
import { FormDialog } from "ui/form-dialog";
import { IconButton } from "ui/icon-button";
import { Typography } from "ui/typography";

import { Select } from "ui/select";
import { TextField } from "ui/text-field";
import { Switch } from "ui/switch";
import CreativeFormFields from "components/modals/creative-modal/components/creative-form-fields";

import { TouchpointType } from "types/touchpoint";

import { useEffect, useMemo } from "react";
import { useCreateTouchpointFromProgramTemplate } from "components/modals/create-touchpoint-from-program-template-modal/hooks/use-create-touchpoint-from-program-template";
import {
  CreateTouchpointFromProgramTemplateModalProps,
  CreatedTouchpointSummary,
} from "components/modals/create-touchpoint-from-program-template-modal/create-touchpoint-from-program-template-modal.types";

import Icons from "assets";
import { getFYOptions } from "utilities";

const SUMMARY_LIST_SX: SxProps<Theme> = { display: "flex", flexDirection: "column", gap: 2 };

const PAGER_SX: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: 1,
};

const TEMPLATE_NAME_LABEL_SX: SxProps<Theme> = { fontWeight: 700 };

const CreateTouchpointFromProgramTemplateModal = ({
  isOpen,
  onClose,
  onSuccess,
  selectedTemplates,
}: CreateTouchpointFromProgramTemplateModalProps) => {
  const {
    methods,
    submitState,
    handleSubmit,
    handleCancelClick,
    setSelectedCampaign,
    isMulti,
    currentIndex,
    totalItems,
    goNext,
    goPrev,
    isAllValid,
    campaignOptions,
    isCampaignsLoading,
    contentTypeOptions,
    contentTypes,
    classTypeOptions,
    populationTypeOptions,
    campaigns,
    touchpointLabel,
    createdTouchpoints,
    isEmail,
  } = useCreateTouchpointFromProgramTemplate({
    isOpen,
    onClose,
    onSuccess,
    selectedTemplates,
  });

  const navigate = useNavigate();
  const { control, watch } = methods;
  const selectedCampaignId = watch("campaignId");

  const showSummary = createdTouchpoints.length > 1;

  // Group touchpoints by campaign name, then by folder name
  const groupedTouchpoints = useMemo(() => {
    const campaignMap = new Map<string, Map<string, CreatedTouchpointSummary[]>>();

    for (const tp of createdTouchpoints) {
      if (!campaignMap.has(tp.campaignName)) {
        campaignMap.set(tp.campaignName, new Map());
      }
      const folderMap = campaignMap.get(tp.campaignName)!;
      const folderKey = tp.folderName || "";
      if (!folderMap.has(folderKey)) {
        folderMap.set(folderKey, []);
      }
      folderMap.get(folderKey)!.push(tp);
    }

    return campaignMap;
  }, [createdTouchpoints]);

  useEffect(() => {
    if (createdTouchpoints.length === 1) {
      navigate(createdTouchpoints[0].link);
      handleCancelClick();
    }
  }, [createdTouchpoints, navigate, handleCancelClick]);

  const fyOptions = getFYOptions();

  if (!isOpen) return null;

  // =========================================================================
  // Summary screen – shown after successful creation of multiple touchpoints
  // (single touchpoint creation redirects directly to the preview page)
  // =========================================================================
  if (showSummary) {
    return (
      <Dialog
        open
        intent="success"
        size="medium"
        onClose={handleCancelClick}
        icon={<Icons.Creatives />}
        headline="Creatives created"
        description="You may open existing touchpoints in new browser tabs."
        actions={
          <DialogActions
            primary={
              <Button size="large" onClick={handleCancelClick}>
                Close summary
              </Button>
            }
          />
        }>
        <Box sx={SUMMARY_LIST_SX}>
          {[...groupedTouchpoints.entries()].map(([campaignName, folderMap]) => (
            <div key={campaignName} className="flex flex-col gap-1">
              <span className="text-sm text-gray-900">{campaignName}</span>

              {[...folderMap.entries()].map(([folderName, touchpoints]) => (
                <div key={folderName} className="flex flex-col gap-0.5 ml-1">
                  {!!folderName && (
                    <span className="flex items-center gap-1.5 text-xs text-gray-900">
                      <Icons.Folder className="size-3.5 text-gray-400" aria-hidden="true" />
                      {folderName}
                    </span>
                  )}

                  {touchpoints.map((tp) => {
                    const TpIcon =
                      tp.type === TouchpointType.EMAIL ? Icons.Email : Icons.LandingPage;
                    return (
                      <Link
                        key={tp.id}
                        to={tp.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 ml-1 text-sm font-semibold text-aqua-600 hover:text-aqua-700 hover:underline">
                        <TpIcon className="size-4 text-gray-600 shrink-0" />
                        <span className="truncate">{tp.name}</span>

                        <Icons.Forward className="size-3 shrink-0" />
                      </Link>
                    );
                  })}
                </div>
              ))}
            </div>
          ))}
        </Box>
      </Dialog>
    );
  }

  // =========================================================================
  // Form screen – create touchpoints
  // =========================================================================
  return (
    <FormDialog
      open
      onClose={handleCancelClick}
      icon={<Icons.Creatives />}
      headline={
        isMulti ? "Create from templates" : `Create ${touchpointLabel.toLowerCase()} from template`
      }
      description={
        <>
          <Typography component="span" variant="inherit" sx={TEMPLATE_NAME_LABEL_SX}>
            Template name:
          </Typography>{" "}
          {selectedTemplates?.[currentIndex]?.name}
        </>
      }
      form={methods}
      onSubmit={handleSubmit}
      submitDisabled={!isAllValid}
      submitting={submitState === "loading"}
      submitButtonText={
        isMulti ? "Create from templates" : `Create ${touchpointLabel.toLowerCase()}`
      }
      submitLoadingMessage={
        isMulti ? "Creating from templates..." : `Creating ${touchpointLabel.toLowerCase()}...`
      }>
      {isMulti && (
        <Box sx={PAGER_SX}>
          <Typography component="span" variant="body2">
            Showing {currentIndex + 1} of {totalItems} items
          </Typography>
          <IconButton
            size="small"
            variant="contained"
            onClick={goPrev}
            disabled={currentIndex === 0}
            aria-label="Previous item">
            <Icons.ChevronLeft className="size-4" />
          </IconButton>
          <IconButton
            size="small"
            variant="contained"
            onClick={goNext}
            disabled={currentIndex === totalItems - 1}
            aria-label="Next item">
            <Icons.ChevronRight className="size-4" />
          </IconButton>
        </Box>
      )}

      <Controller
        name="campaignId"
        control={control}
        render={({ field: { value, onChange, ...field } }) => (
          <Select
            {...field}
            label="Campaign name"
            selectName="campaignId"
            ariaLabel="Select campaign"
            placeholder={isCampaignsLoading ? "Loading campaigns..." : "Select..."}
            required
            disabled={isCampaignsLoading}
            options={campaignOptions}
            value={campaignOptions.find((option) => option.value === value) || null}
            onChange={(option) => {
              const newCampaignId = String(option?.value ?? "");
              setSelectedCampaign(
                campaigns.find((campaign) => campaign.id === newCampaignId) ?? null,
              );
              onChange(newCampaignId);
            }}
            startIcon={<Icons.Search className="text-aqua-600" />}
          />
        )}
      />

      <Controller
        name="name"
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            label={`${touchpointLabel} name`}
            required
            errMessage={fieldState.error?.message}
            {...field}
          />
        )}
      />

      <CreativeFormFields
        populationTypeOptions={populationTypeOptions}
        contentTypeOptions={contentTypeOptions}
        classTypeOptions={classTypeOptions}
        fyOptions={fyOptions}
        isContentTypeLocked={contentTypes?.length === 1}
        resetKey={selectedCampaignId}>
        {isEmail && (
          <div className="flex items-center gap-3">
            <Switch
              toggleName="Enable client theme"
              checked={selectedTemplates?.[currentIndex]?.latestVersion?.applyClientTheme ?? false}
              disabled={true}
            />
            <span className="text-sm text-gray-900">Theme set by template</span>
          </div>
        )}
      </CreativeFormFields>
    </FormDialog>
  );
};

export default CreateTouchpointFromProgramTemplateModal;
